import React, { FormEvent, useEffect, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';
import { ClipboardIcon } from '@heroicons/react/24/outline';
import { templateService } from '../../../services/TemplateService';
import { useGetTemplate, useUpdateTemplate } from '../../../hooks';
import Tags from '@yaireo/tagify/dist/react.tagify';
import { ErrorsSummery } from '../atoms/ErrorsSummery';
import { HTMLFormElementExtended } from '../../../utils/form-urils';
import { TemplateModel } from '../../../models/template.model';
import { Button, Card, Modal } from 'flowbite-react';
import dayjs from 'dayjs';
import { renderAsync } from 'docx-preview';
import { TemplateTester } from '../molecules/TemplateTester';

class TemplateForm extends TemplateModel {
  public template?: FileList;
}

HTMLFormElement.prototype.getValues = function () {
  const obj: any = {};

  for (let i = 0; i < this.length; i++) {
    const formElement = this[i] as HTMLInputElement;

    if (formElement.name)
      if (formElement.type === 'file') obj[formElement.name] = formElement.files;
      else obj[formElement.name] = formElement.value;
  }

  return obj;
};

function formatBytes(bytes: number, si = false) {
  const unitValue = si ? 1000 : 1024; // Use 1000 for SI (kB, MB) or 1024 for IEC (KiB, MiB)
  const units = si
    ? ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

  if (bytes === 0) {
    return '0 B';
  }

  const i = Math.floor(Math.log(bytes) / Math.log(unitValue));
  return `${(bytes / Math.pow(unitValue, i)).toFixed(2)} ${units[i]}`;
}

function formatTemplateObject(templateObject?: string) {
  if (!templateObject) return '';

  try {
    return JSON.stringify(JSON.parse(templateObject), null, 2);
  } catch {
    return templateObject;
  }
}

function hardenPreviewLinks(previewContainer: HTMLDivElement) {
  previewContainer.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
    try {
      const url = new URL(link.href, window.location.origin);

      if (!['http:', 'https:', 'mailto:'].includes(url.protocol)) {
        link.removeAttribute('href');
        return;
      }

      link.target = '_blank';
      link.rel = 'noopener noreferrer';
    } catch {
      link.removeAttribute('href');
    }
  });
}

const documentPreviewOptions = {
  breakPages: true,
  ignoreLastRenderedPageBreak: false,
  ignoreFonts: true,
  renderAltChunks: false,
  renderComments: false,
  renderChanges: false,
  renderEndnotes: true,
  renderFooters: true,
  renderHeaders: true,
  renderFootnotes: true,
  useBase64URL: false,
};

async function renderDocumentPreview(
  file: Blob,
  previewContainer: HTMLDivElement,
  isCurrent: () => boolean,
): Promise<boolean> {
  const stagingContainer = document.createElement('div');
  await renderAsync(file, stagingContainer, stagingContainer, documentPreviewOptions);

  if (!isCurrent()) {
    return false;
  }

  previewContainer.replaceChildren(...Array.from(stagingContainer.childNodes));
  hardenPreviewLinks(previewContainer);
  return true;
}

function TemplateDetails() {
  const params = useParams();
  const templateId = params['id'] as string;
  const {
    data: template,
    isFetched,
    refetch: refetchTemplate,
  } = useGetTemplate(params['id'] as string);
  const updateTemplate = useUpdateTemplate();
  const [errors, setErrors] = useState<string[]>([]);
  const previewRef = useRef<HTMLDivElement>(null);
  const previewRequestVersion = useRef(0);
  const [previewError, setPreviewError] = useState<string>();
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [isTemplateTesterOpen, setIsTemplateTesterOpen] = useState(false);
  const [testTemplateFile, setTestTemplateFile] = useState<Blob>();
  const [isTestTemplateLoading, setIsTestTemplateLoading] = useState(false);
  const [testTemplateError, setTestTemplateError] = useState<string>();
  const testTemplateRequestVersion = useRef(0);

  useEffect(() => {
    if (!isFetched || !previewRef.current) {
      return;
    }

    const previewContainer = previewRef.current;
    const requestVersion = ++previewRequestVersion.current;
    const isCurrent = () => requestVersion === previewRequestVersion.current;

    setIsPreviewLoading(true);
    setPreviewError(undefined);
    previewContainer.replaceChildren();

    templateService
      .download(templateId)
      .then((file) => renderDocumentPreview(file, previewContainer, isCurrent))
      .catch(() => {
        if (isCurrent()) {
          setPreviewError(
            'The document preview could not be generated. You can still download the template.',
          );
        }
      })
      .finally(() => {
        if (isCurrent()) {
          setIsPreviewLoading(false);
        }
      });

    return () => {
      if (isCurrent()) {
        previewRequestVersion.current += 1;
      }
    };
  }, [isFetched, previewVersion, templateId]);

  useEffect(
    () => () => {
      testTemplateRequestVersion.current += 1;
    },
    [],
  );

  const openTemplateTester = () => {
    const requestVersion = ++testTemplateRequestVersion.current;
    setIsTemplateTesterOpen(true);
    setTestTemplateFile(undefined);
    setTestTemplateError(undefined);
    setIsTestTemplateLoading(true);

    templateService
      .download(templateId)
      .then((file) => {
        if (requestVersion === testTemplateRequestVersion.current) {
          setTestTemplateFile(file);
        }
      })
      .catch(() => {
        if (requestVersion === testTemplateRequestVersion.current) {
          setTestTemplateError('The template could not be loaded for testing.');
        }
      })
      .finally(() => {
        if (requestVersion === testTemplateRequestVersion.current) {
          setIsTestTemplateLoading(false);
        }
      });
  };

  const closeTemplateTester = () => {
    testTemplateRequestVersion.current += 1;
    setIsTemplateTesterOpen(false);
    setTestTemplateFile(undefined);
    setTestTemplateError(undefined);
    setIsTestTemplateLoading(false);
  };

  const formSubmitHandler = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    var form = ev.target as HTMLFormElementExtended;
    const template = form.getValues<TemplateForm>();

    template.tags =
      template.tags && template.tags.length
        ? (JSON.parse(template.tags!) as [{ value: string }]).map((x) => x.value).join(',')
        : undefined;

    const validationErrors = validateForm(template);
    setErrors(validationErrors);

    if (validationErrors.length) {
      return;
    }

    try {
      await updateTemplate.mutateAsync({
        id: templateId,
        template,
        file: template.template ? template.template[0] : undefined,
      });
      await refetchTemplate();

      if (template.template?.[0]) {
        setPreviewVersion((version) => version + 1);
      }
    } catch {
      setErrors(['Unable to update the template. Please try again.']);
    }
  };

  const validateForm = (template: TemplateForm) => {
    const localErrors = [];
    if (!template.name || !template.name.length || template.name.length < 5)
      localErrors.push('Name cannot be empty or less then 5 characters');

    return localErrors;
  };

  return (
    <div>
      {isFetched ? (
        <div>
          <div className="w-full text-sm mb-5">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 min-[1300px]:grid-cols-3">
              <div className="template-fields border-solid border-grey-200 p-3">
                <div className="mb-3 flex items-center">
                  <h2 className="text-xl text-white">Update form</h2>
                </div>
                <form
                  name="template-form"
                  className="w-full max-w-lg"
                  onSubmit={(ev) => formSubmitHandler(ev)}
                >
                  <div className="flex flex-wrap -mx-3 mb-3">
                    <div className="w-full px-3">
                      <label
                        className="block uppercase tracking-wide text-gray-300 text-xs font-bold mb-2"
                        htmlFor="name"
                      >
                        Name <small>should have at least 5 leters</small>
                      </label>
                      <input
                        className="appearance-none block w-full border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                        name="name"
                        type="text"
                        minLength={5}
                        defaultValue={template?.template?.name || ''}
                        placeholder="Type in template name, aka 'Customer order'"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap -mx-3 mb-3">
                    <div className="w-full px-3">
                      <label className="block mb-2 text-sm font-medium text-gray-300 dark:text-white">
                        Tags
                      </label>

                      <Tags
                        className="w-full template-tags"
                        showDropdown={false}
                        defaultValue={template?.template?.tags}
                        name="tags"
                        placeholder="Tags"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="block mb-2 text-sm font-medium text-gray-300 dark:text-white">
                      Upload .docx template
                    </label>

                    <div className="relative w-full">
                      <input
                        name="template"
                        className="
                          border-gray-200 shadow-sm rounded-md 
                          focus:z-10 focus:border-blue-500 focus:ring-blue-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400
                          file:bg-transparent file:border-0
                          file:bg-gray-100 file:mr-4
                          file:py-3 file:px-4
                          dark:file:bg-gray-700 dark:file:text-gray-400
                            
                          block w-full z-20 text-sm text-gray-900 bg-gray-50  
                          border-m-gray-100 border-m-2 border border-gray-300 focus:ring-blue-500
                          focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 
                          dark:placeholder-gray-400 dark:text-white dark:focus:border-blue-500"
                        type="file"
                        accept="application/msword,
                        application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      />
                    </div>
                  </div>

                  <div className="flex w-full text-right">
                    <button
                      type="submit"
                      className="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold
                            text-white shadow-sm hover:bg-green-700 sm:ml-3 sm:w-auto ml-auto"
                    >
                      Update
                    </button>
                  </div>

                  {errors.length ? <ErrorsSummery errors={errors} /> : ''}
                </form>
              </div>
              <div className="file-info border-solid border-grey-200 p-3">
                <div className="mb-3 flex items-center">
                  <h2 className="text-xl text-white">Template file details</h2>
                </div>
                <dl className="divide-y divide-white/10">
                  <div className="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt className="text-sm/6 font-medium text-gray-100">Template ID</dt>
                    <dd className="mt-1 break-all text-sm/6 text-gray-400 sm:col-span-2 sm:mt-0">
                      {templateId}
                    </dd>
                  </div>
                  <div className="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt className="text-sm/6 font-medium text-gray-100">File name</dt>
                    <dd className="mt-1 text-sm/6 text-gray-400 sm:col-span-2 sm:mt-0">
                      {template.fileInfo.objectName}
                    </dd>
                  </div>
                  <div className="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt className="text-sm/6 font-medium text-gray-100">Size</dt>
                    <dd className="mt-1 text-sm/6 text-gray-400 sm:col-span-2 sm:mt-0">
                      {formatBytes(template.fileInfo.size)}
                    </dd>
                  </div>
                  <div className="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt className="text-sm/6 font-medium text-gray-100">Content Type</dt>
                    <dd className="mt-1 text-sm/6 text-gray-400 sm:col-span-2 sm:mt-0">
                      {template.fileInfo.contentType}
                    </dd>
                  </div>
                  <div className="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt className="text-sm/6 font-medium text-gray-100">Last modified</dt>
                    <dd className="mt-1 text-sm/6 text-gray-400 sm:col-span-2 sm:mt-0">
                      {dayjs(template.fileInfo.lastModified).format('YYYY-MM-DD HH:mm:ss')}
                    </dd>
                  </div>
                </dl>
              </div>
              <div className="template-actions border-solid border-grey-200 p-3 md:col-span-2 min-[1300px]:col-span-1">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h2 className="text-xl text-white">Template Object</h2>
                  <Button
                    type="button"
                    className="hidden shrink-0 rounded-md bg-green-600 text-sm font-semibold text-white shadow-sm hover:bg-green-700 min-[1300px]:inline-flex"
                    onClick={openTemplateTester}
                  >
                    Test template
                  </Button>
                </div>

                <Card style={{ overflowY: 'scroll', maxHeight: '19em' }} className="relative">
                  <Button
                    outline
                    className="absolute top-2 right-2 text-gray-900"
                    onClick={() => navigator.clipboard.writeText(template.template.templateObject)}
                  >
                    <ClipboardIcon className="h-5 w-5" />
                  </Button>
                  <code className="language-json">
                    <pre>{formatTemplateObject(template.template?.templateObject)}</pre>
                  </code>
                </Card>
                {/* <div className="flex mt-3">
                  <div className="mr-3">
                    <Button>Edit</Button>
                  </div>
                  <div>
                    <Button color="red">Delete</Button>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
          <section
            className="mt-5 hidden min-[1300px]:block"
            aria-labelledby="document-preview-heading"
          >
            <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h1 id="document-preview-heading" className="text-xl font-bold text-white">
                {template.template.name} preview
              </h1>
              <p className="text-sm text-gray-400">
                Preview only — fonts may differ from the original document.
              </p>
            </div>
            <Card className="document-preview bg-white text-gray-900">
              {isPreviewLoading && <p>Generating document preview…</p>}
              {previewError && <p className="text-red-600">{previewError}</p>}
              <div
                ref={previewRef}
                className="max-h-[calc(100vh-9rem)] overflow-auto overscroll-contain bg-gray-200"
                aria-label={`${template.name} document preview`}
              />
            </Card>
          </section>
        </div>
      ) : (
        <div>Loading...</div>
      )}
      {isFetched && (
        <Modal show={isTemplateTesterOpen} onClose={closeTemplateTester} size="7xl">
          <Modal.Header>Test template</Modal.Header>
          <Modal.Body className="max-h-[calc(100vh-8rem)] overflow-y-auto">
            {isTestTemplateLoading && <p className="text-white">Loading template…</p>}
            {testTemplateError && <p className="text-red-400">{testTemplateError}</p>}
            {testTemplateFile && (
              <TemplateTester
                initialTemplate={testTemplateFile}
                templateName={template.fileInfo.objectName}
                templateKey={`${templateId}:${previewVersion}`}
                initialData={template.template?.templateObject}
                showTemplateUpload={false}
              />
            )}
          </Modal.Body>
        </Modal>
      )}
      {/* <div style={{ display: 'none' }}> */}
    </div>
  );
}

export default TemplateDetails;
